/**
 * PDF Field Mappings — Centralized config defining what data each PDF needs.
 *
 * Source types:
 *   agency        — from agencies table (name, address, phone, npiNumber, logoUrl)
 *   patient       — from patients table (firstName, lastName, dob, gender, phone, address, language)
 *   patient_profile — from patient_profiles table (allergies, emergencyContacts, physician, etc.)
 *   visit         — from visits table (clockInAt, clockOutAt, serviceType, frequency, status, startOfCareDate)
 *   rn            — from agency_members / rns (firstName, lastName, credentials)
 *   assessment_question — a question answered by the RN in the Assessment phase, matched by section + text
 *   auto_computed  — a question with visibleOnMobile=false + AI trigger with trigger_point=post_visit
 *   medication    — from visit_medications table
 *   diagnosis     — from visit_diagnoses table (with classification filter)
 *   signature     — from visit_signatures table (signerType filter)
 *   poc_duty      — from visit_poc_duties table
 *   supervisory   — from supervisory evaluation tables (task_ratings, competency_ratings, notes)
 *   static_text   — hardcoded text, no DB source needed
 */
// ═══════════════════════════════════════════════════════════
// AUTO-COMPUTED OPTION LISTS (shared across PDFs)
// ═══════════════════════════════════════════════════════════
export const FUNCTIONAL_LIMITATIONS_OPTIONS = [
    { value: "Amputation", cmsCode: "1" },
    { value: "Bowel/Bladder (Incontinence)", cmsCode: "2" },
    { value: "Contracture", cmsCode: "3" },
    { value: "Hearing", cmsCode: "4" },
    { value: "Paralysis", cmsCode: "5" },
    { value: "Endurance", cmsCode: "6" },
    { value: "Ambulation", cmsCode: "7" },
    { value: "Speech", cmsCode: "8" },
    { value: "Legally Blind", cmsCode: "9" },
    { value: "Dyspnea with Minimal Exertion", cmsCode: "A" }
    // "Other" is the free-text overflow field, cmsCode "B"
];
export const ACTIVITIES_PERMITTED_OPTIONS = [
    { value: "Complete Bedrest", cmsCode: "1" },
    { value: "Bedrest BRP", cmsCode: "2" },
    { value: "Up As Tolerated", cmsCode: "3" },
    { value: "Transfer Bed/Chair", cmsCode: "4" },
    { value: "Exercises Prescribed", cmsCode: "5" },
    { value: "Partial Weight Bearing", cmsCode: "6" },
    { value: "Independent At Home", cmsCode: "7" },
    { value: "Crutches", cmsCode: "8" },
    { value: "Cane", cmsCode: "9" },
    { value: "Wheelchair", cmsCode: "A" },
    { value: "Walker", cmsCode: "B" },
    { value: "No Restrictions", cmsCode: "C" }
    // "Other" is the free-text overflow field, cmsCode "D"
];
export const FALL_RISK_FACTOR_OPTIONS = [
    { value: "Age 65+" },
    { value: "History of falls in past 3 months" },
    { value: "3+ medical diagnoses" },
    { value: "Taking 4+ medications" },
    { value: "Impaired balance/gait" },
    { value: "Visual impairment" },
    { value: "Cognitive impairment" },
    { value: "Urinary frequency/incontinence" },
    { value: "Use of assistive device" },
    { value: "Environmental hazards present" }
];
export const SAFETY_MEASURES_OPTIONS = [
    { value: "Fall Precautions" },
    { value: "Medication Safety" },
    { value: "Skin/Pressure Prevention" },
    { value: "Infection Control" },
    { value: "Emergency Plan" },
    { value: "Environment" },
    { value: "Aspiration Precautions" },
    { value: "Oxygen Safety" },
    { value: "Diabetes Precautions" },
    { value: "Anticoagulant Precautions" },
    { value: "Cognitive Impairment/Wandering Precautions" },
    { value: "Seizure/Syncope Precautions" },
    { value: "DME/Transfer Safety" }
];
// ═══════════════════════════════════════════════════════════
// 1. PATIENT ASSESSMENT
// ═══════════════════════════════════════════════════════════
const patientAssessment = {
    id: "patient_assessment",
    name: "Patient Assessment",
    description: "Full clinical assessment — all body systems, vitals, meds, diagnoses (7-9 pages)",
    pageCount: 9,
    fields: [
        // --- Header ---
        { name: "Agency Name", source: "agency", field: "name" },
        { name: "Agency Address", source: "agency", field: "address" },
        { name: "Agency Logo", source: "agency", field: "logoUrl" },
        // --- Patient Information ---
        { name: "Patient Name", source: "patient", field: "firstName+lastName" },
        { name: "DOB", source: "patient", field: "dob" },
        { name: "Gender", source: "patient", field: "gender" },
        { name: "Address", source: "patient", field: "address" },
        { name: "Phone", source: "patient", field: "phone" },
        { name: "Alt Phone", source: "patient", field: "phoneNumbers" },
        { name: "Primary Language", source: "patient", field: "language" },
        { name: "Clinician Name", source: "rn", field: "firstName+lastName" },
        {
            name: "Living Arrangement",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "What is the patient's living arrangement?"
        },
        // --- Visit Information ---
        { name: "Date of Visit", source: "visit", field: "clockInAt", notes: "Date part only" },
        { name: "Time In", source: "visit", field: "clockInAt", notes: "Time part only" },
        { name: "Time Out", source: "visit", field: "clockOutAt", notes: "Time part only" },
        {
            name: "Type of Assessment",
            source: "visit",
            field: "visitType",
            notes: "SOC / Reassessment / Supervisory — from visit type, not a question"
        },
        // --- Visit Info (assessment questions) ---
        {
            name: "Caregiver Present",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Is the caregiver present?"
        },
        {
            name: "Others in House",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Is there anyone else in the house related to the patient?"
        },
        {
            name: "Relationship",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Relationship to the patient"
        },
        {
            name: "Hospitalized in Last 3 Months",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Was the patient hospitalized in the last 3 months?"
        },
        {
            name: "Hospitalization Date",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Date of hospitalization"
        },
        {
            name: "Hospitalization Reason",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Reason for hospitalization"
        },
        {
            name: "Hospital Stay Duration",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "How long was the hospital stay?"
        },
        {
            name: "Post-Hospitalization Condition",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Patient condition post hospitalization"
        },
        {
            name: "Fall History",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Did the patient have a history of falls in the past 3 months?"
        },
        {
            name: "Number of Falls",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Number of falls in the past 3 months"
        },
        {
            name: "Fall Injury",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Was the patient injured?"
        },
        {
            name: "Injury Type",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Type of injury"
        },
        {
            name: "Assistive Device",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Does the patient use an assistive device?"
        },
        {
            name: "Fall — Physician Notified",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Was the physician notified?"
        },
        {
            name: "Fall — Physician Orders",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Physician response/orders"
        },
        {
            name: "Fall Prevention Education",
            source: "assessment_question",
            section: "Visit Info",
            questionText: "Patient/family educated on fall prevention and calling 911 for head injury or suspected fracture?"
        },
        // --- Auto-Computed (Page 1) ---
        {
            name: "Priority Code",
            source: "auto_computed",
            questionText: "Priority Code",
            page: "1",
            notes: "Level 1 (High) / Level 2 (Moderate) / Level 3 (Low)"
        },
        {
            name: "TAL Status",
            source: "auto_computed",
            questionText: "TAL Status",
            page: "1",
            notes: "5 options: TAL-1 Stretcher/Vent/Bariatric, TAL-2 Wheelchair, TAL-3 Ambulatory"
        },
        // --- Vital Signs ---
        {
            name: "Temperature Method",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Body temperature — Method"
        },
        {
            name: "Temperature",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Body temperature (°F)"
        },
        {
            name: "BP Method",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Blood pressure — Method"
        },
        {
            name: "Blood Pressure",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Blood Pressure (Systolic/Diastolic)"
        },
        {
            name: "Pulse Method",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Pulse rate — Method"
        },
        {
            name: "Pulse",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Pulse rate (bpm)"
        },
        {
            name: "Resp Method",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Respiration rate — Method"
        },
        {
            name: "Respirations",
            source: "assessment_question",
            section: "Vital Signs",
            questionText: "Respiration rate (breaths/min)"
        },
        {
            name: "Vital Signs Narrative",
            source: "section_narrative",
            section: "Vital Signs"
        },
        // --- Fall Risk Assessment (Auto-Computed) ---
        {
            name: "Fall Risk Factors",
            source: "auto_computed",
            questionText: "Fall Risk Assessment",
            page: "1",
            options: FALL_RISK_FACTOR_OPTIONS,
            renderAs: "checkboxes",
            notes: "JSON: { factors: string[], score: number, level: string }. 10 checkboxes + score + level badge"
        },
        // Fall Risk has no separate section — it's an auto-computed field (Q71) in Auto-Computed Fields
        // --- Neurological ---
        {
            name: "Level of Consciousness",
            source: "assessment_question",
            section: "Neurological",
            questionText: "Level of Consciousness: How would you describe the patient's level of alertness today?"
        },
        {
            name: "Orientation",
            source: "assessment_question",
            section: "Neurological",
            questionText: "Orientation: The patient is oriented to:"
        },
        {
            name: "Mood/Behavior",
            source: "assessment_question",
            section: "Neurological",
            questionText: "Does the patient have any mood or behavior findings?"
        },
        {
            name: "Neuro Symptoms",
            source: "assessment_question",
            section: "Neurological",
            questionText: "Does the patient experience any neurological symptoms?"
        },
        { name: "Neuro Narrative", source: "section_narrative", section: "Neurological" },
        { name: "Neuro Teaching", source: "section_teaching", section: "Neurological" },
        // --- EENT ---
        {
            name: "Head/Face",
            source: "assessment_question",
            section: "EENT (Eyes, Ears, Nose, Throat)",
            questionText: "Does the patient have any head or face symptoms?"
        },
        {
            name: "Eyes/Vision",
            source: "assessment_question",
            section: "EENT (Eyes, Ears, Nose, Throat)",
            questionText: "Does the patient have any eye or vision conditions?"
        },
        {
            name: "Ears/Hearing",
            source: "assessment_question",
            section: "EENT (Eyes, Ears, Nose, Throat)",
            questionText: "Does the patient have any hearing difficulties, conditions, or use hearing devices?"
        },
        {
            name: "Mouth/Throat/Speech",
            source: "assessment_question",
            section: "EENT (Eyes, Ears, Nose, Throat)",
            questionText: "Does the patient experience any mouth, throat, or speech symptoms?"
        },
        {
            name: "Vision Aids",
            source: "assessment_question",
            section: "EENT (Eyes, Ears, Nose, Throat)",
            questionText: "Does the patient use any vision aids?"
        },
        {
            name: "Hearing Devices",
            source: "assessment_question",
            section: "EENT (Eyes, Ears, Nose, Throat)",
            questionText: "Hearing Devices"
        },
        {
            name: "EENT Narrative",
            source: "section_narrative",
            section: "EENT (Eyes, Ears, Nose, Throat)"
        },
        {
            name: "EENT Teaching",
            source: "section_teaching",
            section: "EENT (Eyes, Ears, Nose, Throat)"
        },
        // --- Cardiopulmonary ---
        {
            name: "Heart Symptoms",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Does the patient experience any heart or circulation symptoms?"
        },
        {
            name: "Edema Location",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Edema Location"
        },
        {
            name: "Edema Grade",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Edema Grade"
        },
        {
            name: "CV Devices",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Does the patient currently use any cardiovascular devices?"
        },
        {
            name: "Lung Symptoms",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Does the patient experience any breathing or lung symptoms?"
        },
        {
            name: "Respiratory Devices",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Does the patient use any respiratory support devices?"
        },
        {
            name: "Lung Sounds",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "What are the patient's lung sounds on assessment?"
        },
        {
            name: "Oxygen Saturation",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Oxygen saturation (SpO2 %)"
        },
        {
            name: "Oxygen Delivery Method",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "How is oxygen being delivered?"
        },
        {
            name: "Oxygen Flow Rate",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Specify oxygen flow rate (LPM)"
        },
        {
            name: "Oxygen Frequency",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Specify oxygen frequency"
        },
        {
            name: "Intermittent Oxygen Schedule",
            source: "assessment_question",
            section: "Cardiopulmonary",
            questionText: "Intermittent oxygen schedule"
        },
        { name: "Cardio Narrative", source: "section_narrative", section: "Cardiopulmonary" },
        { name: "Cardio Teaching", source: "section_teaching", section: "Cardiopulmonary" },
        // --- Musculoskeletal ---
        {
            name: "MSK Symptoms",
            source: "assessment_question",
            section: "Musculoskeletal",
            questionText: "Does the patient experience any muscle, bone, or joint symptoms?"
        },
        { name: "MSK Narrative", source: "section_narrative", section: "Musculoskeletal" },
        { name: "MSK Teaching", source: "section_teaching", section: "Musculoskeletal" },
        // --- Pain ---
        {
            name: "Pain Present",
            source: "assessment_question",
            section: "Pain",
            questionText: "In the last 3 days, has the patient complained of pain or shown any signs of pain?"
        },
        {
            name: "Pain Intensity",
            source: "assessment_question",
            section: "Pain",
            questionText: "In the last 3 days, what was the highest level of pain the patient experienced?"
        },
        {
            name: "Pain Characteristics",
            source: "assessment_question",
            section: "Pain",
            questionText: "What are the pain characteristics?"
        },
        {
            name: "Pain Duration",
            source: "assessment_question",
            section: "Pain",
            questionText: "How long does the patient's pain usually last?"
        },
        {
            name: "Pain Location",
            source: "assessment_question",
            section: "Pain",
            questionText: "Where is the pain located?"
        },
        {
            name: "Pain Relief",
            source: "assessment_question",
            section: "Pain",
            questionText: "What relieves the pain?"
        },
        {
            name: "Pain Mgmt Effective",
            source: "assessment_question",
            section: "Pain",
            questionText: "From the patient's point of view, are the current methods adequately controlling their pain?"
        },
        {
            name: "Pain After Intervention",
            source: "assessment_question",
            section: "Pain",
            questionText: "Pain Intensity After Intervention"
        },
        {
            name: "Non-Verbal Pain",
            source: "assessment_question",
            section: "Pain",
            questionText: "In the last 3 days, which of the following non-verbal pain behaviors have been observed?"
        },
        { name: "Pain Narrative", source: "section_narrative", section: "Pain" },
        // --- GI/GU/Reproductive ---
        {
            name: "GI Symptoms",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient experience any stomach, bowel, or digestive symptoms?"
        },
        {
            name: "Ostomy",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient have an ostomy?"
        },
        {
            name: "GU Symptoms",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient experience any bladder or urinary symptoms?"
        },
        {
            name: "Female Reproductive",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient have any female reproductive conditions, breast problems, or history?"
        },
        {
            name: "Male Reproductive",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient have any male reproductive conditions, symptoms, or history?"
        },
        {
            name: "Urinary Catheter",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient have a urinary catheter?"
        },
        {
            name: "Catheter Type & Location",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Urinary Catheter (type & location)"
        },
        {
            name: "Dialysis",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Does the patient receive dialysis treatments (on an ongoing basis)?"
        },
        {
            name: "Ostomy Type & Location",
            source: "assessment_question",
            section: "GI/GU/Reproductive",
            questionText: "Ostomy (type & location)"
        },
        { name: "GI/GU Narrative", source: "section_narrative", section: "GI/GU/Reproductive" },
        { name: "GI/GU Teaching", source: "section_teaching", section: "GI/GU/Reproductive" },
        // --- Integumentary / Hematopoietic / Endocrine ---
        {
            name: "Skin Symptoms",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Does the patient currently have any skin symptoms or skin problems?"
        },
        {
            name: "Wound Location",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Wound Location"
        },
        {
            name: "Wound Size",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Wound Size"
        },
        {
            name: "Wound Appearance",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Wound Appearance"
        },
        {
            name: "Wound Stage",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Wound Stage"
        },
        {
            name: "Blood Conditions",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Does the patient have any blood-related conditions or problems?"
        },
        {
            name: "Endocrine",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Does the patient have any hormone or gland-related conditions?"
        },
        // Diabetic Education removed — not a separate question on staging; education is captured in teaching_text
        {
            name: "Blood Glucose",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Blood Glucose Level"
        },
        {
            name: "Skin Color",
            source: "assessment_question",
            section: "Integumentary / Hematopoietic / Endocrine",
            questionText: "Skin Color on exam:"
        },
        {
            name: "Integ Narrative",
            source: "section_narrative",
            section: "Integumentary / Hematopoietic / Endocrine"
        },
        {
            name: "Integ Teaching",
            source: "section_teaching",
            section: "Integumentary / Hematopoietic / Endocrine"
        },
        // --- Nutritional Assessment ---
        {
            name: "Appetite",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Select the most accurate option for the patient's appetite over the last 3 days:"
        },
        {
            // Renders as the "Body Metrics" row. Two questions feed it for as
            // long as the version gate stands: new app builds answer this one,
            // older builds and every historical visit answer the legacy
            // "Weight" entry below. Both stay listed so the correction bot can
            // anchor a weight ask from either.
            name: "Body Metrics",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Body metrics (weight, height, BMI):",
            notes: "Weight, height, BMI and clinical category in one answer"
        },
        {
            name: "Weight",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Current weight (lbs):"
        },
        {
            name: "Weight Trend",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Compared with their usual weight over the past few months, the patient's weight is:"
        },
        {
            name: "Fluid Restriction",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Is the patient on a prescribed fluid restriction?"
        },
        {
            name: "Food Intake",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Over the last 3 days, how has the patient been eating and drinking?"
        },
        {
            name: "Special Medical Diet",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient follow any special medical diet?"
        },
        {
            name: "Nutrient Restrictions",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have specific nutrient restrictions?"
        },
        {
            name: "Food Textures",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient need any special food textures?"
        },
        {
            name: "Medication Allergies",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have any medication allergies?"
        },
        {
            name: "Food Allergies",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have any food allergies?"
        },
        {
            name: "Environmental Allergies",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have any substance or environmental allergies?"
        },
        {
            name: "Nutrition Narrative",
            source: "section_narrative",
            section: "Allergies and Nutrition"
        },
        {
            name: "Nutrition Teaching",
            source: "section_teaching",
            section: "Allergies and Nutrition"
        },
        // --- Environment and Safety ---
        {
            name: "Environmental Hazards",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Are there any environmental or safety hazards in the patient's home?"
        },
        {
            name: "Environment Narrative",
            source: "section_narrative",
            section: "Safety, Emergency and Immunization"
        },
        {
            name: "Environment Teaching",
            source: "section_teaching",
            section: "Safety, Emergency and Immunization"
        },
        // --- DME and Supplies ---
        {
            name: "Mobility Equipment",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient currently use any mobility equipment?"
        },
        {
            name: "Bathroom Safety",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient have or use any bathroom safety equipment?"
        },
        {
            name: "Bedroom Equipment",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient have or use any bedroom or positioning equipment?"
        },
        {
            name: "Medical Supplies",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient regularly use any medical supplies at home?"
        },
        { name: "DME Narrative", source: "section_narrative", section: "DME and Supplies" },
        { name: "DME Teaching", source: "section_teaching", section: "DME and Supplies" },
        // --- Emergency and Advance Directives ---
        {
            name: "Has Emergency Contact",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Does the patient have a local emergency contact?"
        },
        {
            name: "Emergency Contact Name",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Contact person full name"
        },
        {
            name: "Emergency Contact Address",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Address"
        },
        {
            name: "Emergency Contact Phone",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Phone"
        },
        {
            name: "Primary Physician",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Patient Physician"
        },
        {
            name: "Emergency Plan Discussed",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Emergency Plan Discussed?"
        },
        {
            name: "Emergency Preparedness",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Emergency Preparedness"
        },
        {
            // Distinct from "Emergency Preparedness" above on purpose. That one
            // is a readiness checklist whose "Medical alert device if needed"
            // tick means the device is part of the plan; this records whether
            // the patient actually has one and whether it works. A correction
            // filed against either must anchor to its own question.
            name: "Personal Emergency Response System",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Does the patient have a personal emergency response system (PERS) in the home?",
            notes: "PERS presence and usability; resolves the alert-device checklist item"
        },
        {
            name: "Advance Directives",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Advance Directives"
        },
        // --- Immunizations ---
        {
            name: "Influenza Immunization",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Did the patient get the influenza immunization?"
        },
        {
            name: "Influenza Date",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "influenza immunization date"
        },
        {
            name: "Pneumonia Immunization",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Did the patient get the Pneumonia immunization?"
        },
        {
            name: "Pneumonia Date",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Pneumonia immunization date"
        },
        {
            name: "COVID-19 Immunization",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Did the patient get the COVID-19 immunization?"
        },
        {
            name: "COVID-19 Date",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "COVID-19 immunization date"
        },
        {
            name: "Other Immunization",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Did the patient get other immunization?"
        },
        {
            name: "Other Immunization Name",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Other immunization — Name"
        },
        {
            name: "Other Immunization Date",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Other immunization — Date"
        },
        {
            name: "Refused Immunizations",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Did the patient refuse immunizations?"
        },
        {
            name: "Immunization Education",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Educational info provided?"
        },
        // --- Consent & Signatures ---
        {
            name: "POC Discussed",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Plan of Care Discussed?"
        },
        {
            name: "Teaching Understanding",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Patient/Caregiver verbalized understanding of all teaching provided?"
        },
        {
            name: "Rep Signature Name",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Representative Signature - Full Name"
        },
        {
            name: "Emergency Teaching",
            source: "section_teaching",
            section: "Safety, Emergency and Immunization"
        },
        // --- Medications (Page 6) ---
        {
            name: "Medications Table",
            source: "medication",
            notes: "Each row: name plus strength, ordered dose, frequency, admin notes"
        },
        { name: "Medication Teaching", source: "section_teaching", section: "Medications" },
        { name: "Medication Narrative", source: "section_narrative", section: "Medications" },
        // --- Diagnosis Information (Page 6) ---
        { name: "Principal Diagnosis", source: "diagnosis", classification: "principal" },
        { name: "Surgical Diagnosis", source: "diagnosis", classification: "surgical" },
        { name: "Other Diagnoses", source: "diagnosis", classification: "other" },
        // --- Auto-Computed (Page 7) ---
        {
            name: "Functional Limitations",
            source: "auto_computed",
            questionText: "Functional Limitations",
            page: "7",
            options: FUNCTIONAL_LIMITATIONS_OPTIONS,
            renderAs: "checkboxes",
            hasOtherField: true,
            notes: "10 checkboxes (1-A) + Other (B) free-text. Output 'None' array if no limitations."
        },
        {
            name: "Safety Measures",
            source: "auto_computed",
            questionText: "Safety Measures",
            page: "7",
            options: SAFETY_MEASURES_OPTIONS,
            renderAs: "item_list",
            hasOtherField: true,
            notes: "Listed as item list on Patient Assessment"
        },
        {
            name: "Activities Permitted",
            source: "auto_computed",
            questionText: "Activities Permitted",
            page: "7",
            options: ACTIVITIES_PERMITTED_OPTIONS,
            renderAs: "checkboxes",
            hasOtherField: true,
            notes: "12 checkboxes (1-D). Manual-only: BedrestBRP, UpAsTolerated, PartialWeightBearing, IndependentAtHome, ExercisesPrescribed."
        },
        // --- Progress Note (Page 8) ---
        {
            name: "Progress Note",
            source: "auto_computed",
            questionText: "Progress Note",
            page: "8",
            notes: "5-6 line AI-generated clinical narrative paragraph"
        },
        // --- Signatures ---
        { name: "RN Signature", source: "signature", signerType: "rn" },
        { name: "Patient Signature", source: "signature", signerType: "patient" }
    ]
};
// ═══════════════════════════════════════════════════════════
// 2. CMS 485
// ═══════════════════════════════════════════════════════════
const cms485 = {
    id: "cms_485",
    name: "CMS 485",
    description: "Federal Home Health Certification form — 28 numbered boxes + addendum",
    pageCount: 2,
    fields: [
        // --- Boxes 1-9: Header info ---
        {
            name: "Box 1 — HI Claim No.",
            source: "static_text",
            notes: "Left empty per user decision"
        },
        { name: "Box 2 — SOC Date", source: "visit", field: "startOfCareDate" },
        {
            name: "Box 3 — Cert Period",
            source: "visit",
            field: "certificationPeriodStart+certificationPeriodEnd",
            notes: "From patients table cert period fields"
        },
        {
            name: "Box 4 — Medical Record No.",
            source: "static_text",
            notes: "Left empty per user decision"
        },
        { name: "Box 5 — Provider No. (NPI)", source: "agency", field: "npiNumber" },
        {
            name: "Box 6 — Patient Name & Address",
            source: "patient",
            field: "firstName+lastName+address"
        },
        { name: "Box 7 — Provider Name & Address", source: "agency", field: "name+address+phone" },
        { name: "Box 8 — DOB", source: "patient", field: "dob" },
        { name: "Box 9 — Sex", source: "patient", field: "gender", notes: "M/F checkboxes" },
        // --- Box 10: Medications ---
        {
            name: "Box 10 — Medications",
            source: "medication",
            notes: "Each medication: Dose/Frequency/Route, (N)ew/(C)hanged flag"
        },
        // --- Boxes 11-13: Diagnoses ---
        {
            name: "Box 11 — Principal Diagnosis",
            source: "diagnosis",
            classification: "principal",
            notes: "ICD code + description + date"
        },
        {
            name: "Box 12 — Surgical Procedure",
            source: "diagnosis",
            classification: "surgical",
            notes: "ICD code + description + date"
        },
        {
            name: "Box 13 — Other Diagnoses",
            source: "diagnosis",
            classification: "other",
            notes: "Multiple ICD codes with descriptions"
        },
        // --- Boxes 14-17: Assessment cross-refs ---
        {
            name: "Box 14 — DME and Supplies",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient currently use any mobility equipment?",
            notes: "Merge all 4 DME questions into one list"
        },
        {
            name: "Box 15 — Safety Measures",
            source: "auto_computed",
            questionText: "Safety Measures",
            options: SAFETY_MEASURES_OPTIONS,
            renderAs: "checkboxes",
            hasOtherField: true,
            notes: "Checkbox list on CMS 485. Overflows to Addendum Item 15."
        },
        {
            name: "Box 16 — Nutritional Req.",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient follow any special medical diet?",
            notes: "Merge 3 nutrition check questions"
        },
        {
            name: "Box 17 — Allergies",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have any medication allergies?",
            notes: "Format: 'Med: X. Food: Y. Substance/Environmental: Z' — merge 3 allergy questions"
        },
        // --- Boxes 18-20: Auto-computed ---
        {
            name: "Box 18A — Functional Limitations",
            source: "auto_computed",
            questionText: "Functional Limitations",
            options: FUNCTIONAL_LIMITATIONS_OPTIONS,
            renderAs: "checkboxes",
            hasOtherField: true,
            notes: "Numbered 1-A checkboxes + B=Other per federal form layout"
        },
        {
            name: "Box 18B — Activities Permitted",
            source: "auto_computed",
            questionText: "Activities Permitted",
            options: ACTIVITIES_PERMITTED_OPTIONS,
            renderAs: "checkboxes",
            hasOtherField: true,
            notes: "Numbered 1-C checkboxes + D=Other per federal form layout"
        },
        {
            name: "Box 19 — Mental Status",
            source: "assessment_question",
            section: "Neurological",
            questionText: "Level of Consciousness: How would you describe the patient's level of alertness today?",
            notes: "Numbered 1-8 checkboxes per federal form. Also uses Orientation question."
        },
        {
            name: "Box 20 — Prognosis",
            source: "auto_computed",
            questionText: "Prognosis",
            notes: "1-Poor, 2-Guarded, 3-Fair, 4-Good, 5-Excellent"
        },
        // --- Boxes 21-22: Static text ---
        {
            name: "Box 21 — Orders for Discipline",
            source: "static_text",
            notes: "Standard orders boilerplate. Append: 'Service Type: {serviceType}. Aide Frequency: {frequency}' from visits table"
        },
        {
            name: "Box 22 — Goals of Care",
            source: "static_text",
            notes: "Standard goals boilerplate + discharge planning"
        },
        // --- Boxes 23-28: Signatures & footer ---
        { name: "Box 23 — RN Signature + Date", source: "signature", signerType: "rn" },
        {
            name: "Box 24 — Physician Name & Address",
            source: "patient_profile",
            field: "physician"
        },
        {
            name: "Box 25 — Date HHA Received POT",
            source: "visit",
            field: "clockInAt",
            notes: "Same as visit date"
        },
        {
            name: "Box 26 — Certification Statement",
            source: "static_text",
            notes: "Federal certification language"
        },
        {
            name: "Box 27 — Physician Signature",
            source: "static_text",
            notes: "Left blank for physician to sign later"
        },
        // --- Addendum ---
        {
            name: "Addendum — Item 15 overflow",
            source: "auto_computed",
            questionText: "Safety Measures",
            options: SAFETY_MEASURES_OPTIONS,
            renderAs: "comma_text",
            notes: "Full text if overflows Box 15"
        },
        {
            name: "Addendum — Item 21 overflow",
            source: "static_text",
            notes: "Full orders text + service type + aide frequency"
        },
        { name: "Addendum — Item 22 overflow", source: "static_text", notes: "Full goals text" },
        { name: "Addendum — RN Signature", source: "signature", signerType: "rn" }
    ]
};
// ═══════════════════════════════════════════════════════════
// 3. EMERGENCY KARDEX
// ═══════════════════════════════════════════════════════════
const emergencyKardex = {
    id: "emergency_kardex",
    name: "Emergency Kardex",
    description: "Quick-reference emergency card — contacts, allergies, DME, directives (2 pages)",
    pageCount: 2,
    fields: [
        // --- Header ---
        { name: "Agency Name", source: "agency", field: "name" },
        { name: "Agency Address", source: "agency", field: "address" },
        { name: "Agency Logo", source: "agency", field: "logoUrl" },
        // --- Patient Header ---
        { name: "Clinician Name", source: "rn", field: "firstName+lastName" },
        { name: "Patient Name", source: "patient", field: "firstName+lastName" },
        { name: "Gender", source: "patient", field: "gender" },
        { name: "DOB", source: "patient", field: "dob" },
        { name: "Mobile Phone", source: "patient", field: "phone" },
        {
            name: "Home Phone",
            source: "patient",
            field: "phoneNumbers",
            notes: "Home phone from phoneNumbers array"
        },
        { name: "Address", source: "patient", field: "address" },
        // --- Static text ---
        {
            name: "Evacuation Warning",
            source: "static_text",
            notes: "'If you are evacuated, this form MUST be brought to the shelter'"
        },
        { name: "Level 1 Description", source: "static_text" },
        { name: "Level 2 Description", source: "static_text" },
        { name: "Level 3 Description", source: "static_text" },
        // --- Auto-Computed ---
        {
            name: "Priority Code",
            source: "auto_computed",
            questionText: "Priority Code",
            notes: "Level 1/2/3 — highlights matching level"
        },
        {
            name: "TAL Status",
            source: "auto_computed",
            questionText: "TAL Status",
            notes: "5 checkboxes"
        },
        // --- Patient Profile cross-refs ---
        {
            name: "Emergency Contacts",
            source: "patient_profile",
            field: "emergencyContacts",
            notes: "From patient_profiles.emergencyContacts JSON array"
        },
        {
            name: "DME Items",
            source: "patient_profile",
            field: "dmeItems",
            notes: "From patient_profiles.dmeItems JSON array"
        },
        // --- Assessment cross-refs (Page 1) ---
        {
            name: "Emergency Contact",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Contact person full name",
            notes: "Also needs Address + Phone questions from same section"
        },
        {
            name: "Primary Physician",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Patient Physician"
        },
        {
            name: "Advance Directives",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Advance Directives"
        },
        {
            name: "Nutritional Requirements",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient follow any special medical diet?",
            notes: "Merge 3 nutrition check questions into one checklist"
        },
        {
            name: "Allergies",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have any medication allergies?",
            notes: "Merge 3 allergy questions"
        },
        // --- Page 2 ---
        {
            name: "DME and Supplies",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient currently use any mobility equipment?",
            notes: "Merge all 4 DME questions + add extra Kardex items (Bed Rails, BiPAP, Tracheostomy, etc.)"
        },
        {
            name: "Shelter Items List",
            source: "static_text",
            notes: "Bullet list: 7-10 days supply, dietary foods, blankets, etc."
        },
        {
            name: "Advance Directives Acknowledgement",
            source: "static_text",
            notes: "Legal acknowledgement paragraph with agency name"
        },
        {
            name: "Emergency Plan Discussed",
            source: "assessment_question",
            section: "Safety, Emergency and Immunization",
            questionText: "Emergency Plan Discussed?"
        },
        // --- Signature ---
        { name: "RN Signature", source: "signature", signerType: "rn" },
        { name: "RN Name", source: "rn", field: "firstName+lastName" },
        { name: "Visit Date", source: "visit", field: "clockInAt", notes: "Date part only" }
    ]
};
// ═══════════════════════════════════════════════════════════
// 4. PLAN OF CARE
// ═══════════════════════════════════════════════════════════
const planOfCare = {
    id: "plan_of_care",
    name: "Plan of Care",
    description: "Duty checklist with frequencies + patient info summary + emergency guidelines (1 page)",
    pageCount: 1,
    fields: [
        // --- Header ---
        { name: "Agency Name", source: "agency", field: "name" },
        { name: "Agency Address", source: "agency", field: "address" },
        { name: "Agency Logo", source: "agency", field: "logoUrl" },
        { name: "Agency Phone", source: "agency", field: "phone" },
        // --- Patient Header ---
        { name: "Patient Name", source: "patient", field: "firstName+lastName" },
        { name: "Patient Address", source: "patient", field: "address" },
        { name: "Gender", source: "patient", field: "gender" },
        { name: "DOB", source: "patient", field: "dob" },
        {
            name: "Cert Period",
            source: "visit",
            field: "certificationPeriodStart+certificationPeriodEnd"
        },
        // --- POC Duties (6 groups) ---
        {
            name: "Personal Care Duties",
            source: "poc_duty",
            notes: "Bath, Hair, Mouth, Grooming, Dressing, Skin, Foot — each with selected + frequency"
        },
        {
            name: "Toileting Duties",
            source: "poc_duty",
            notes: "Incontinence Supplies, Bedpan, Toilet, Commode, Incontinence"
        },
        {
            name: "Nutrition Duties",
            source: "poc_duty",
            notes: "Meals (B/L/D/Snack), Feeding, Record Intake, Prescribed Diet"
        },
        {
            name: "Activities Duties",
            source: "poc_duty",
            notes: "Transferring, Walking, Assistive Devices, Exercise, Positioning"
        },
        {
            name: "Treatment Duties",
            source: "poc_duty",
            notes: "Foley, Medication Reminders, Pain, Observe Changes, Safety, Ostomy"
        },
        {
            name: "Household Duties",
            source: "poc_duty",
            notes: "Linens, Laundry, Housekeeping, Shopping, Appointments"
        },
        // --- Emergency Reporting Guidelines ---
        {
            name: "Emergency Guidelines",
            source: "static_text",
            notes: "Call 911, Bleeding, Blood Sugar, Heart/Lung, Infection, etc."
        },
        // --- General Patient Info ---
        {
            name: "Services Needed",
            source: "visit",
            field: "serviceType",
            notes: "PCA / HHA / AHHA"
        },
        {
            name: "Aide Frequency",
            source: "visit",
            field: "frequency",
            notes: "e.g. '1 DAY 6 HOURS'"
        },
        {
            name: "Mental Status",
            source: "assessment_question",
            section: "Neurological",
            questionText: "Level of Consciousness: How would you describe the patient's level of alertness today?",
            notes: "Comma-separated on POC"
        },
        {
            name: "Allergies",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient have any medication allergies?",
            notes: "Merge 3 allergy questions, comma-separated"
        },
        {
            name: "Safety Measures",
            source: "auto_computed",
            questionText: "Safety Measures",
            options: SAFETY_MEASURES_OPTIONS,
            renderAs: "warning_tags",
            hasOtherField: false,
            notes: "Comma-separated warning tags on POC"
        },
        {
            name: "Activities Permitted",
            source: "auto_computed",
            questionText: "Activities Permitted",
            options: ACTIVITIES_PERMITTED_OPTIONS,
            renderAs: "comma_text",
            hasOtherField: false,
            notes: "Comma-separated text on POC (not checkboxes)"
        },
        {
            name: "Nutritional Requirements",
            source: "assessment_question",
            section: "Allergies and Nutrition",
            questionText: "Does the patient follow any special medical diet?",
            notes: "Merge 3 nutrition questions, comma-separated"
        },
        {
            name: "Devices Used",
            source: "assessment_question",
            section: "DME and Supplies",
            questionText: "Does the patient currently use any mobility equipment?",
            notes: "Selected items only, comma-separated"
        },
        // --- Special Instructions ---
        { name: "Special Instructions", source: "poc_duty", field: "specialInstructions" },
        // --- Signature ---
        { name: "RN Signature", source: "signature", signerType: "rn" },
        { name: "RN Name", source: "rn", field: "firstName+lastName" },
        { name: "Visit Date", source: "visit", field: "clockInAt", notes: "Date part only" }
    ]
};
// ═══════════════════════════════════════════════════════════
// 5. SUPERVISORY EVALUATION
// ═══════════════════════════════════════════════════════════
const supervisoryEvaluation = {
    id: "supervisory_evaluation",
    name: "Supervisory Evaluation",
    description: "Caregiver evaluation — task ratings + competencies + observations (2 pages)",
    pageCount: 2,
    fields: [
        // --- Header ---
        { name: "Agency Name", source: "agency", field: "name" },
        { name: "Agency Address", source: "agency", field: "address" },
        { name: "Agency Logo", source: "agency", field: "logoUrl" },
        // --- Patient Info ---
        { name: "Patient Name", source: "patient", field: "firstName+lastName" },
        { name: "DOB", source: "patient", field: "dob" },
        { name: "Gender", source: "patient", field: "gender" },
        // The aide/caregiver NAME shown on the evaluation (patient pill + next to the
        // caregiver signature). The render prefers supervisory.actual_aide_name (who was
        // actually present and signed) over the visit's assigned caregiver, so the
        // correctable source is supervisory — agency-directable via
        // set_supervisory_field(field:"aide_name", …), NOT set_answer.
        {
            name: "Caregiver Name",
            source: "supervisory",
            field: "actualAideName",
            notes: 'Actual aide/caregiver name (actual_aide_name, falls back to visit.caregiver_name). Agency-directable — set_supervisory_field(field:"aide_name", value, askId).'
        },
        {
            name: "Aide Name",
            source: "supervisory",
            field: "actualAideName",
            notes: 'Alias of Caregiver Name — actual_aide_name. Agency-directable — set_supervisory_field(field:"aide_name", value, askId).'
        },
        { name: "Supervisor Name", source: "rn", field: "firstName+lastName" },
        { name: "Visit Date", source: "visit", field: "clockInAt", notes: "Date part only" },
        {
            name: "Is Aide Present",
            source: "supervisory",
            field: "isAidePresent",
            notes: 'Whether the aide was present this visit. Agency-directable — set_supervisory_field(field:"aide_present", value, askId).'
        },
        // --- Task Ratings (~40 tasks) ---
        {
            name: "Supervisory Task Ratings",
            source: "supervisory",
            field: "taskRatings",
            notes: "~40 tasks, each: meets_expectations / needs_improvement / n_a"
        },
        // --- Competency Ratings (~15 items) ---
        {
            name: "Core Competency Ratings",
            source: "supervisory",
            field: "competencyRatings",
            notes: "~15 competencies, each: meets_expectations / needs_improvement / n_a"
        },
        // --- Observations (Page 2) ---
        { name: "Observation Notes", source: "supervisory", field: "observationNotes" },
        { name: "Supervisor Comments", source: "supervisory", field: "supervisorComments" },
        // --- Signatures ---
        { name: "Supervisor Signature", source: "signature", signerType: "rn" },
        { name: "Supervisor Name & Title", source: "rn", field: "firstName+lastName+credentials" },
        { name: "Date", source: "visit", field: "clockInAt" },
        {
            name: "Caregiver Signature",
            source: "static_text",
            notes: "Left blank for caregiver to sign"
        },
        {
            name: "Caregiver Name",
            source: "supervisory",
            field: "actualAideName",
            notes: 'Actual aide/caregiver name next to the signature (actual_aide_name). Agency-directable — set_supervisory_field(field:"aide_name", value, askId).'
        }
    ]
};
// ═══════════════════════════════════════════════════════════
// 6. WELCOME PACKAGE
// ═══════════════════════════════════════════════════════════
const welcomePackage = {
    id: "welcome_package",
    name: "Welcome Package",
    description: "Legal documents, patient rights, emergency info, signatures (8 pages)",
    pageCount: 8,
    fields: [
        // --- Header (every page) ---
        { name: "Agency Name", source: "agency", field: "name" },
        { name: "Agency Address", source: "agency", field: "address" },
        { name: "Agency Logo", source: "agency", field: "logoUrl" },
        { name: "Agency Phone", source: "agency", field: "phone" },
        // --- Patient Info ---
        { name: "Patient Name", source: "patient", field: "firstName+lastName" },
        { name: "Patient Address", source: "patient", field: "address" },
        // --- Content (mostly static) ---
        {
            name: "Authorization Text",
            source: "static_text",
            notes: "Legal authorization with agency name inserted"
        },
        { name: "Patient Rights", source: "static_text", notes: "Full patient rights list" },
        {
            name: "Patient Responsibilities",
            source: "static_text",
            notes: "Full responsibilities list"
        },
        { name: "HIPAA Notice", source: "static_text", notes: "Privacy practices" },
        {
            name: "Emergency Preparedness",
            source: "static_text",
            notes: "Emergency kit checklist + evacuation plan"
        },
        { name: "Complaint Procedure", source: "static_text", notes: "How to file complaints" },
        {
            name: "Advance Directives Info",
            source: "static_text",
            notes: "NY Proxy Law information"
        },
        // --- Signatures ---
        { name: "Patient Signature", source: "signature", signerType: "patient" },
        { name: "RN Signature", source: "signature", signerType: "rn" },
        // The acknowledgment is a consent, so the signature caption must name
        // whoever actually signed. Declaring these two makes resolvePdfData
        // fetch the assessment answers for this document; without them
        // `assessmentAnswers` is empty here and the block falls back to the
        // patient's name, which printed a minor's name on the consent line.
        {
            name: "Signer Role",
            source: "assessment_question",
            field: "Select who is signing for the patient",
            notes: "Q142 - drives the Patient vs Representative signature caption"
        },
        {
            name: "Representative Name",
            source: "assessment_question",
            field: "Representative Signature - Full Name",
            notes: "Q148 - printed instead of the patient name when a representative signed"
        },
        { name: "Visit Date", source: "visit", field: "clockInAt" }
    ]
};
// ═══════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════
/**
 * Synthetic mapping used only by the PDF-validation worker: it references EVERY
 * data source so `resolvePdfData` loads the complete dataset ONCE per visit. That
 * single resolved object is then fed to every document's validation prompt (each
 * prompt still projects only its own blocks via the formatter's per-doc INCLUDE).
 * One load, all data, all prompts.
 */
const allValidationData = {
    id: "all_validation",
    name: "All Validation Data",
    description: "Loads every source once for the PDF-validation ground truth (fed to all doc prompts).",
    pageCount: 0,
    fields: [
        { name: "Agency", source: "agency" },
        { name: "Patient", source: "patient" },
        { name: "Patient Profile", source: "patient_profile" },
        { name: "Visit", source: "visit" },
        { name: "RN", source: "rn" },
        { name: "Assessment Answers", source: "assessment_question" },
        { name: "Auto-Computed", source: "auto_computed" },
        { name: "Medications", source: "medication" },
        { name: "Diagnoses", source: "diagnosis" },
        { name: "Signatures", source: "signature" },
        { name: "POC Duties", source: "poc_duty" },
        { name: "Supervisory", source: "supervisory" },
        { name: "Section Narratives", source: "section_narrative" },
        { name: "Section Teaching", source: "section_teaching" },
        { name: "Branch Follow-up Summaries", source: "branch_followup_summary" }
    ]
};
export const PDF_MAPPINGS = [
    patientAssessment,
    cms485,
    emergencyKardex,
    planOfCare,
    supervisoryEvaluation,
    welcomePackage,
    allValidationData
];
/** Get a mapping by ID */
export function getPdfMapping(id) {
    return PDF_MAPPINGS.find((m) => m.id === id);
}
//# sourceMappingURL=pdf-field-mappings.js.map